import { Anchor, AppShell, Burger, Container, Group, Image, Text, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { normalizeLanguage } from "../i18n";
import { getCurrentUser, isDistributor } from "../shared/getCurrentUser";
import { imgLogoNoText128 } from "../shared/images";
import { CountryFlag } from "./CountryFlag";
import { MarketingFooter } from "./MarketingFooter";
import { OfflineNotification } from "./OfflineNotification";

type Props = {
  title?: string;
  children: React.ReactNode;
  /** Skip default Container wrapping (for full-bleed marketing heroes) */
  fullBleed?: boolean;
  /** Show the shared marketing footer */
  showFooter?: boolean;
};

const navLinkProps = {
  c: "gray.2" as const,
  underline: "never" as const,
  fw: 500,
};

export function NavBarContainer({
  title,
  children,
  fullBleed = false,
  showFooter = false,
}: Props) {
  const { t, i18n } = useTranslation();
  const [opened, { toggle, close }] = useDisclosure(false);
  const currentUser = getCurrentUser();
  const showDistributorPortal = !!currentUser && isDistributor();
  const currentLocale = normalizeLanguage(i18n.resolvedLanguage ?? i18n.language);

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 260,
        breakpoint: "sm",
        collapsed: { desktop: true, mobile: !opened },
      }}
      padding={fullBleed ? 0 : "md"}
      styles={{
        main: {
          backgroundColor: "var(--mantine-color-gray-0)",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <AppShell.Header bg="charcoal.8" style={{ borderBottom: "1px solid var(--mantine-color-charcoal-7)" }}>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              color="white"
            />
            <Anchor component={Link} to="/" underline="never">
              <Text span fw={700} c="gray.3" style={{ fontFamily: "var(--mantine-font-family-headings)" }}>
                Din
              </Text>
              <Text span fw={700} c="brand.5" style={{ fontFamily: "var(--mantine-font-family-headings)" }}>
                Label
              </Text>
            </Anchor>
            <Anchor component={Link} to="/">
              <Image src={imgLogoNoText128} alt={t("nav.logoAlt")} w={40} h={40} />
            </Anchor>
          </Group>

          <Group gap="md" visibleFrom="sm" wrap="nowrap">
            <Anchor component={Link} to="/install" {...navLinkProps}>
              {t("nav.download")}
            </Anchor>
            <Anchor
              component={Link}
              to="/buy"
              c="brand.5"
              underline="never"
              fw={600}
            >
              {t("nav.buy")}
            </Anchor>
            <Anchor component={Link} to="/tutorials" {...navLinkProps}>
              {t("nav.tutorials")}
            </Anchor>
            <Anchor component={Link} to="/barcodes" {...navLinkProps}>
              {t("nav.barcodes")}
            </Anchor>
            <Anchor
              component={Link}
              to="/language"
              {...navLinkProps}
              aria-label={t("nav.language")}
              title={t("nav.language")}
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              <CountryFlag locale={currentLocale} size={22} title={t("nav.language")} />
            </Anchor>
            {showDistributorPortal && (
              <Anchor component={Link} to="/distributorportal" {...navLinkProps}>
                {t("nav.distributorPortal")}
              </Anchor>
            )}
            {currentUser ? (
              <>
                <Anchor component={Link} to="/userinfo" {...navLinkProps}>
                  {t("nav.myAccount")}
                </Anchor>
                <Anchor href="/logout" {...navLinkProps}>
                  {t("nav.logout")}
                </Anchor>
              </>
            ) : (
              <Anchor component={Link} to="/signin" {...navLinkProps}>
                {t("nav.login")}
              </Anchor>
            )}
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md" bg="charcoal.9">
        <Anchor component={Link} to="/install" onClick={close} py="xs" c="gray.2">
          {t("nav.download")}
        </Anchor>
        <Anchor component={Link} to="/buy" onClick={close} py="xs" c="brand.5" fw={600}>
          {t("nav.buy")}
        </Anchor>
        <Anchor component={Link} to="/tutorials" onClick={close} py="xs" c="gray.2">
          {t("nav.tutorials")}
        </Anchor>
        <Anchor component={Link} to="/barcodes" onClick={close} py="xs" c="gray.2">
          {t("nav.barcodes")}
        </Anchor>
        <Anchor
          component={Link}
          to="/language"
          onClick={close}
          py="xs"
          c="gray.2"
          aria-label={t("nav.language")}
          style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          <CountryFlag locale={currentLocale} size={22} />
          {t("nav.language")}
        </Anchor>
        {showDistributorPortal && (
          <Anchor component={Link} to="/distributorportal" onClick={close} py="xs" c="gray.2">
            {t("nav.distributorPortal")}
          </Anchor>
        )}
        {currentUser ? (
          <>
            <Anchor component={Link} to="/userinfo" onClick={close} py="xs" c="gray.2">
              {t("nav.myAccount")}
            </Anchor>
            <Anchor href="/logout" onClick={close} py="xs" c="gray.2">
              {t("nav.logout")}
            </Anchor>
          </>
        ) : (
          <Anchor component={Link} to="/signin" onClick={close} py="xs" c="gray.2">
            {t("nav.login")}
          </Anchor>
        )}
      </AppShell.Navbar>

      <AppShell.Main>
        <div style={{ flex: 1, width: "100%" }}>
          {fullBleed ? (
            <>
              <Container size="lg" pt={0}>
                <OfflineNotification />
              </Container>
              {title && (
                <Container size="lg" pt="md">
                  <Title order={2} mb="md">
                    {title}
                  </Title>
                </Container>
              )}
              {children}
            </>
          ) : (
            <Container size="lg">
              <OfflineNotification />
              {title && (
                <Title order={2} mb="md">
                  {title}
                </Title>
              )}
              {children}
            </Container>
          )}
        </div>
        {showFooter && <MarketingFooter />}
      </AppShell.Main>
    </AppShell>
  );
}
